import * as React from "react";
import { Link, useLocation } from "react-router";
import { useGetIdentity } from "@refinedev/core";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { canAccess } from "@/providers/accessControlProvider";
import { picksCapabilities } from "@/lib/picks/permissions";
import type { AdminIdentity, AdminRole } from "@/providers/types";
import { GuidesSidebarItem } from "@/components/guides/GuidesSidebarItem";

interface NavItem {
  label: string;
  href: string;
  resource: string;
  action: string;
  icon: React.ReactNode;
  badge?: number;
}

const NAV_ITEMS: NavItem[] = [
  {
    label: "Пользователи",
    href: "/users",
    resource: "users",
    action: "list",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    label: "Места",
    href: "/places",
    resource: "places",
    action: "list",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ),
  },
  {
    label: "Картинки локаций",
    href: "/localities",
    resource: "localities",
    action: "list",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <circle cx="8.5" cy="10" r="1.5" />
        <path d="M21 15l-5-5-6 6" />
      </svg>
    ),
  },
  {
    label: "Модерация",
    href: "/moderation",
    resource: "moderation",
    action: "list",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
  },
  {
    label: "Операторы",
    href: "/operators",
    resource: "operators",
    action: "list",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </svg>
    ),
  },
  {
    label: "Расходы",
    href: "/usage",
    resource: "usage",
    action: "list",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
  },
  {
    label: "Журнал аудита",
    href: "/audit",
    resource: "audit",
    action: "list",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="12" y1="18" x2="12" y2="12" />
        <line x1="9" y1="15" x2="15" y2="15" />
      </svg>
    ),
  },
  {
    label: "Дашборд",
    href: "/dashboard",
    resource: "dashboard",
    action: "list",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="3" width="7" height="7" />
        <rect x="14" y="3" width="7" height="7" />
        <rect x="3" y="14" width="7" height="7" />
        <rect x="14" y="14" width="7" height="7" />
      </svg>
    ),
  },
  {
    label: "Импорт",
    href: "/import",
    resource: "import",
    action: "list",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <polyline points="16 16 12 12 8 16" />
        <line x1="12" y1="12" x2="12" y2="21" />
        <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
      </svg>
    ),
  },
];

const PICKS_PARTNERS_PATH = "/picks/partners";
const PICKS_CATEGORIES_PATH = "/picks/categories";

interface PicksNavEntry {
  label: string;
  href: string;
  visible: boolean;
  active: (pathname: string) => boolean;
}

function underPath(pathname: string, base: string): boolean {
  return pathname === base || pathname.startsWith(base + "/");
}

function picksNavEntries(role: AdminRole | undefined): PicksNavEntry[] {
  const caps = picksCapabilities(role);
  const entries: PicksNavEntry[] = [
    {
      label: "Catalog",
      href: "/picks",
      visible: caps.viewCatalog,
      active: (pathname) =>
        underPath(pathname, "/picks") &&
        !underPath(pathname, PICKS_PARTNERS_PATH) &&
        !underPath(pathname, PICKS_CATEGORIES_PATH),
    },
    {
      label: "Partners",
      href: PICKS_PARTNERS_PATH,
      visible: caps.viewPartners,
      active: (pathname) => underPath(pathname, PICKS_PARTNERS_PATH),
    },
    {
      label: "Categories & Icons",
      href: PICKS_CATEGORIES_PATH,
      visible: caps.viewCategories,
      active: (pathname) => underPath(pathname, PICKS_CATEGORIES_PATH),
    },
  ];
  return entries.filter((entry) => entry.visible);
}

export function Sidebar(): React.JSX.Element {
  const location = useLocation();
  const { data: identity } = useGetIdentity<AdminIdentity>();
  const role = identity?.role as AdminRole | undefined;

  const isActive = (href: string): boolean =>
    location.pathname === href || location.pathname.startsWith(href + "/");

  const visibleItems = NAV_ITEMS.filter((item) =>
    canAccess({ resource: item.resource, action: item.action, role }).can
  );

  const picksEntries = picksNavEntries(role);

  return (
    <aside
      className="w-[240px] min-w-[240px] bg-[var(--surface)] border-r border-[var(--border)] flex flex-col fixed top-0 left-0 bottom-0 z-10"
      aria-label="Навигация администратора"
    >
      <div className="px-4 pt-5 pb-3 border-b border-[var(--border)]">
        <div className="text-[20px] font-[700] text-[var(--fg)] tracking-[-0.3px]">
          Jourio
        </div>
        <div className="text-[11px] font-[600] uppercase tracking-[0.8px] text-[var(--muted)] mt-0.5">
          Admin
        </div>
      </div>

      <nav className="flex-1 py-2 overflow-y-auto" aria-label="Разделы">
        <ul role="list">
          {visibleItems.map((item) => {
            const active = isActive(item.href);
            return (
              <li key={item.href}>
                <Link
                  to={item.href}
                  aria-label={
                    item.badge !== undefined && item.badge > 0
                      ? `${item.label}, ${item.badge} элементов требуют внимания`
                      : item.label
                  }
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "flex items-center gap-[10px] px-4 min-h-[36px] py-2 text-[13px] font-[500] border-l-[3px] transition-[background,color] duration-[120ms] no-underline",
                    active
                      ? "bg-[var(--accent-weak)] text-[var(--accent)] border-l-[var(--accent)]"
                      : "text-[var(--muted)] border-l-transparent hover:bg-[var(--surface-2)] hover:text-[var(--fg)]",
                  )}
                >
                  <span
                    className={cn(
                      "w-[18px] h-[18px] flex-shrink-0 [&>svg]:w-full [&>svg]:h-full [&>svg]:stroke-current [&>svg]:stroke-[1.8px] [&>svg]:fill-none",
                    )}
                  >
                    {item.icon}
                  </span>
                  {item.label}
                  {item.badge !== undefined && item.badge > 0 && (
                    <span
                      className="ml-auto bg-[var(--danger)] text-white text-[10px] font-[700] px-1.5 py-px rounded-[10px]"
                      aria-hidden="true"
                    >
                      {item.badge}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
          <GuidesSidebarItem />
          {picksEntries.length > 0 && (
            <li>
              <div className="px-4 pt-3 pb-1 text-[11px] font-[600] uppercase tracking-[0.8px] text-[var(--muted)]">
                Picks
              </div>
              <ul role="list">
                {picksEntries.map((entry) => {
                  const active = entry.active(location.pathname);
                  return (
                    <li key={entry.href}>
                      <Link
                        to={entry.href}
                        aria-current={active ? "page" : undefined}
                        className={cn(
                          "flex items-center gap-[10px] pl-[44px] pr-4 min-h-[36px] py-2 text-[13px] font-[500] border-l-[3px] transition-[background,color] duration-[120ms] no-underline",
                          active
                            ? "bg-[var(--accent-weak)] text-[var(--accent)] border-l-[var(--accent)]"
                            : "text-[var(--muted)] border-l-transparent hover:bg-[var(--surface-2)] hover:text-[var(--fg)]",
                        )}
                      >
                        {entry.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </li>
          )}
        </ul>
      </nav>

      {identity && (
        <div className="px-4 py-[14px] border-t border-[var(--border)]">
          {identity.email && (
            <div className="text-[12px] text-[var(--fg)] font-[500] whitespace-nowrap overflow-hidden text-ellipsis">
              {identity.email}
            </div>
          )}
          {identity.role && (
            <Badge variant="default" className="mt-1">
              {identity.role}
            </Badge>
          )}
        </div>
      )}
    </aside>
  );
}
